import { useQuery } from "@tanstack/react-query";
import { fetchEmailTemplateVariables } from "../../../utils/apiUtils";
import {
    OBSERVABILITY_LOG_PREFIXES,
    TEST_EMAIL_TEMPLATE_QUERY_KEYS,
} from "./constants";

/**
 * TanStack Query hook that fetches the typed variable map (`name → "string" | "boolean"`)
 * and optional subject pre-fill for a specific email template. Enabled only when a
 * non-empty `template` string is provided.
 * Issues a live `GET /notifications/sendDirectEmail/templates/:template/variables` call on every
 * template change (no cache).
 * On load failure, emits a `console.error` and returns `isError: true` — the form disables
 * the Send button and surfaces an inline error with a Retry affordance.
 *
 * @param {string} template The currently selected template id.
 * @returns {{
 *   variables: Record<string, string>,
 *   subject: string | undefined,
 *   isLoading: boolean,
 *   isError: boolean,
 *   refetch: () => void
 * }}
 */
export function useEmailTemplateVariables(template) {
    const query = useQuery({
        queryKey: [TEST_EMAIL_TEMPLATE_QUERY_KEYS.TEMPLATE_VARIABLES_ROOT, template],
        queryFn: () => fetchEmailTemplateVariables(template),
        enabled: Boolean(template),
    });

    if (query.isError) {
        const statusCode = query.error?.cause?.status ?? "";
        const message = query.error?.message ?? "";
        console.error(
            OBSERVABILITY_LOG_PREFIXES.TEMPLATE_VARIABLES_LOAD_FAILED,
            statusCode,
            message
        );
    }

    const variables = query.data?.variables ?? {};
    const subject = query.data?.subject;

    return {
        variables,
        subject,
        isLoading: query.isLoading,
        isError: query.isError,
        refetch: query.refetch,
    };
}
